import { useState, useEffect } from 'react'
import { Sliders, Search, Info, Save, Loader2 } from 'lucide-react'
import { rrGetCaps, rrUpdateCaps, rrGetCapsHistory } from '@/actions/rr'
import { useUser } from '@/contexts/UserContext'
import { toastMessage } from '@/utils/toast-message'

const AV_COLORS = ['#2f9e6b', '#3b82c4', '#b9772e', '#8a5cd1', '#c2557a', '#2aa1a8']
function initials(n: string) { const p = n.trim().split(' '); return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() }
function avColor(name: string) { let h = 0; for (const c of name) h = (h * 31 + c.charCodeAt(0)) % AV_COLORS.length; return AV_COLORS[h] }

type BucketKey = 'abc' | 'reorder' | 'rto' | 'edd' | 'ndr'
type CapKey = 'daily' | 'newLead' | 'notConnected'

interface AgentCaps { daily: string; newLead: string; notConnected: string }
type CapsState = Record<number, Record<BucketKey, AgentCaps>>

interface AgentRow { user_id: number; name: string; email: string }
interface CapChange {
  changed_at: string; bucket: string; agent: string | null; changed_by_name: string | null;
  old_daily_cap: number | null; new_daily_cap: number;
  old_new_lead_cap: number | null; new_new_lead_cap: number;
  old_not_connected_cap: number | null; new_not_connected_cap: number;
}
const BUCKET_LABEL: Record<string, string> = { abandoned_cart: 'ABC', reorder: 'Reorder', rto: 'RTO', edd: 'EDD', ndr: 'NDR' }

const BUCKET_API: Record<BucketKey, string> = { abc: 'abandoned_cart', reorder: 'reorder', rto: 'rto', edd: 'edd', ndr: 'ndr' }

const BUCKETS: { key: BucketKey; label: string; sub: string; cls: string }[] = [
  { key: 'abc',     label: 'ABC',     sub: 'Abandoned Cart', cls: 'bg-blue-50 text-blue-700' },
  { key: 'reorder', label: 'Reorder', sub: 'Follow-up',      cls: 'bg-green-50 text-green-700' },
  { key: 'rto',     label: 'RTO',     sub: 'Returned',       cls: 'bg-red-50 text-red-700' },
  { key: 'edd',     label: 'EDD',     sub: 'In Transit',     cls: 'bg-amber-50 text-amber-700' },
  { key: 'ndr',     label: 'NDR',     sub: 'Failed Delivery', cls: 'bg-orange-50 text-orange-700' },
]

const CAP_ROWS: { key: CapKey; label: string; hint: string }[] = [
  { key: 'daily',        label: 'Overall Daily Cap',   hint: 'Max leads per day · resets at midnight' },
  { key: 'newLead',      label: 'New Lead Cap',        hint: 'Max unworked "New" leads held at once' },
  { key: 'notConnected', label: 'Not Connected Cap',   hint: 'Max "Not Connected" in rolling 15-day window' },
]

export default function CapsPage() {
  const [agents, setAgents] = useState<AgentRow[]>([])
  const [caps, setCaps] = useState<CapsState>({})
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setLoading(true)
    rrGetCaps().then(res => {
      interface CapValues { daily_cap: number; new_lead_cap: number; not_connected_cap: number }
      interface ApiCapRow { user_id: number; name: string; email: string; caps: { abandoned_cart?: CapValues; reorder?: CapValues; rto?: CapValues; edd?: CapValues; ndr?: CapValues } }
      const rows: ApiCapRow[] = (res?.data ?? []) as ApiCapRow[]
      const agentList: AgentRow[] = rows.map(r => ({ user_id: r.user_id, name: r.name, email: r.email }))
      setAgents(agentList)
      if (agentList.length) setSelectedIds(new Set([agentList[0].user_id]))

      const state: CapsState = {}
      for (const r of rows) {
        state[r.user_id] = {
          abc:     { daily: String(r.caps.abandoned_cart?.daily_cap ?? 30),     newLead: String(r.caps.abandoned_cart?.new_lead_cap ?? 10),    notConnected: String(r.caps.abandoned_cart?.not_connected_cap ?? 8) },
          reorder: { daily: String(r.caps.reorder?.daily_cap ?? 20),             newLead: String(r.caps.reorder?.new_lead_cap ?? 8),              notConnected: String(r.caps.reorder?.not_connected_cap ?? 6) },
          rto:     { daily: String(r.caps.rto?.daily_cap ?? 15),                 newLead: String(r.caps.rto?.new_lead_cap ?? 6),                  notConnected: String(r.caps.rto?.not_connected_cap ?? 5) },
          edd:     { daily: String(r.caps.edd?.daily_cap ?? 0),                  newLead: String(r.caps.edd?.new_lead_cap ?? 0),                  notConnected: String(r.caps.edd?.not_connected_cap ?? 0) },
          ndr:     { daily: String(r.caps.ndr?.daily_cap ?? 0),                  newLead: String(r.caps.ndr?.new_lead_cap ?? 0),                  notConnected: String(r.caps.ndr?.not_connected_cap ?? 0) },
        }
      }
      setCaps(state)
    }).finally(() => setLoading(false))
  }, [])

  // Only super admins may change caps (the API refuses anyone else with 403). On
  // 25 Sep 2026 five saves were refused with no message on screen, so the team
  // believed EDD/NDR caps were set while every one stayed 0.
  const { userData } = useUser()
  const isSuperAdmin = (userData as { data?: { role?: { role_id?: number } } } | null)?.data?.role?.role_id === 9401

  const [history, setHistory] = useState<CapChange[]>([])
  const [historyFor, setHistoryFor] = useState<number | null>(null)
  async function loadHistory(uid: number | undefined) {
    if (!uid) return
    setHistoryFor(uid)
    try {
      const res = await rrGetCapsHistory(uid) as { data?: CapChange[] } | null
      setHistory(res?.data || [])
    } catch { setHistory([]) }
  }

  function toggleSelect(id: number) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id) }
      else next.add(id)
      return next
    })
  }

  function updateCap(bucket: BucketKey, cap: CapKey, val: string) {
    setCaps(prev => {
      const next = { ...prev }
      selectedIds.forEach(id => {
        next[id] = { ...next[id], [bucket]: { ...next[id][bucket], [cap]: val } }
      })
      return next
    })
  }

  async function handleSave() {
    if (!isSuperAdmin) {
      toastMessage('Only a super admin can change caps. Ask Mahender, Gautam or Rajesh to save them.', 'error')
      return
    }
    if (selectedIds.size === 0) {
      toastMessage('Tick at least one agent on the left first — caps are saved for the ticked agents.', 'error')
      return
    }
    setSaving(true)
    try {
      // An empty/garbage field means "no cap" — never fall back to another
      // bucket's defaults, or clearing an NDR box would silently write ABC's 30.
      const parseCap = (v: string | undefined) => { const n = parseInt(v ?? ''); return isNaN(n) || n < 0 ? 0 : n }
      const entries = [...selectedIds].flatMap(uid =>
        (Object.entries(BUCKET_API) as [BucketKey, string][]).map(([bk, apiKey]) => ({
          user_id: uid,
          bucket: apiKey,
          daily_cap: parseCap(caps[uid]?.[bk]?.daily),
          new_lead_cap: parseCap(caps[uid]?.[bk]?.newLead),
          not_connected_cap: parseCap(caps[uid]?.[bk]?.notConnected),
        }))
      )
      const res = await rrUpdateCaps(entries) as { success?: boolean; message?: string; data?: { changed?: number } } | null
      if (res && res.success === false) throw new Error(res.message || 'Save refused')
      const changed = res?.data?.changed
      toastMessage(`Caps saved for ${selectedIds.size} agent${selectedIds.size > 1 ? 's' : ''}`
        + (changed !== undefined ? ` · ${changed} cap${changed === 1 ? '' : 's'} changed` : ''), 'success')
      loadHistory(activeId)
    } catch (e) {
      const msg = (e as { response?: { status?: number } })?.response?.status === 403
        ? 'Not saved: only a super admin can change caps.'
        : 'Not saved — please try again. Nothing was changed.'
      toastMessage(msg, 'error')
    } finally {
      setSaving(false)
    }
  }

  const filtered = agents.filter(a => !search || a.name.toLowerCase().includes(search.toLowerCase()))
  const activeId = [...selectedIds][selectedIds.size - 1] ?? agents[0]?.user_id
  const activeAgent = agents.find(a => a.user_id === activeId)
  const activeCaps = caps[activeId]
  useEffect(() => { if (activeId && activeId !== historyFor) loadHistory(activeId) }, [activeId]) // eslint-disable-line react-hooks/exhaustive-deps
  const fmtChange = (o: number | null, n: number) => (o === null ? `${n}` : o === n ? `${n}` : `${o} → ${n}`)

  return (
    <div className="h-full flex flex-col">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-brandColor flex items-center justify-center flex-shrink-0">
            <Sliders className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900 tracking-tight">Caps & Limits</h1>
            <p className="text-sm text-gray-500 mt-0.5">Set the three independent workload caps for each agent, per bucket.</p>
            {!isSuperAdmin && (
              <p className="text-xs mt-1 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                View only — caps can be changed by a super admin.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {loading
          ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>
          : <div className="grid grid-cols-[280px_1fr] gap-4 items-start">
            {/* Left — agent list */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Agents</span>
                <span className="text-xs font-bold text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">{agents.length}</span>
              </div>
              <div className="px-3 py-2">
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search agents…" className="w-full pl-8 pr-3 h-8 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-brandColor focus:border-brandColor" />
                </div>
                <div className="space-y-0.5 max-h-[55vh] overflow-y-auto">
                  {filtered.map(a => (
                    <label key={a.user_id} className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg cursor-pointer transition-colors ${selectedIds.has(a.user_id) ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                      <input type="checkbox" checked={selectedIds.has(a.user_id)} onChange={() => toggleSelect(a.user_id)} className="w-4 h-4 accent-brandColor flex-shrink-0" />
                      <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0" style={{ background: avColor(a.name) }}>{initials(a.name)}</span>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">{a.name}</div>
                        <div className="text-xs text-gray-400 truncate">{a.email}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-start gap-2 px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-300" />
                Tick multiple agents to <b className="text-gray-600">bulk-edit</b> the same caps.
              </div>
            </div>

            {/* Right — 3×3 cap grid */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                {activeAgent
                  ? <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ background: avColor(activeAgent.name) }}>{initials(activeAgent.name)}</span>
                    <div>
                      <div className="text-base font-extrabold text-gray-900">{activeAgent.name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{selectedIds.size > 1 ? `Editing ${selectedIds.size} agents` : 'Editing caps · changes apply on save'}</div>
                    </div>
                  </div>
                  : <div />
                }
              </div>

              {activeCaps && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="w-[28%] min-w-[220px] px-5 py-3 text-left" />
                        {BUCKETS.map(b => (
                          <th key={b.key} className="px-3 py-3 text-center">
                            <span className={`text-xs font-extrabold px-2.5 py-1 rounded-lg ${b.cls}`}>{b.label}</span>
                            <div className="text-[10px] font-normal text-gray-400 mt-1">{b.sub}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {CAP_ROWS.map(row => (
                        <tr key={row.key}>
                          <td className="px-5 py-4">
                            <div className="text-sm font-bold text-gray-900">{row.label}</div>
                            <div className="text-xs text-gray-400 mt-0.5">{row.hint}</div>
                          </td>
                          {BUCKETS.map(b => (
                            <td key={b.key} className="px-3 py-4 text-center">
                              <input
                                value={activeCaps[b.key][row.key]}
                                onChange={e => updateCap(b.key, row.key, e.target.value)}
                                readOnly={!isSuperAdmin}
                                title={!isSuperAdmin ? 'Only a super admin can change caps' : selectedIds.size === 0 ? 'Tick an agent on the left to edit' : undefined}
                                className="w-[72px] h-11 text-center border border-gray-200 rounded-xl text-lg font-extrabold text-gray-900 focus:outline-none focus:ring-2 focus:ring-brandColor/30 focus:border-brandColor"
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex items-center gap-2.5 px-5 py-4 border-t border-gray-100 bg-gray-50/60">
                <div className="flex-1" />
                <span className="text-xs text-gray-400">
                  {selectedIds.size ? `Will save ${selectedIds.size} ticked agent${selectedIds.size > 1 ? 's' : ''}` : 'Tick agents on the left to edit and save'}
                </span>
                <button onClick={handleSave} disabled={saving || !isSuperAdmin} className="h-9 px-4 bg-brandColor text-white rounded-lg text-sm font-semibold hover:bg-brand-hover inline-flex items-center gap-2 disabled:opacity-50">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Save caps
                </button>
              </div>

              {/* Who changed what -- logged by the API since 26 Sep 2026 */}
              <div className="px-5 py-4 border-t border-gray-100">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                  Change history{activeAgent ? ` · ${activeAgent.name}` : ''}
                </div>
                {history.length === 0
                  ? <p className="text-xs text-gray-400">No changes recorded yet (recording started 26 Sep 2026).</p>
                  : <table className="w-full text-xs">
                      <thead><tr className="text-gray-400 text-left">
                        <th className="py-1 pr-3 font-semibold">When</th><th className="py-1 pr-3 font-semibold">By</th>
                        <th className="py-1 pr-3 font-semibold">Bucket</th><th className="py-1 pr-3 font-semibold">Daily</th>
                        <th className="py-1 pr-3 font-semibold">New lead</th><th className="py-1 font-semibold">Not connected</th>
                      </tr></thead>
                      <tbody className="divide-y divide-gray-100">
                        {history.map((h, i) => (
                          <tr key={i} className="text-gray-700">
                            <td className="py-1 pr-3 whitespace-nowrap">{new Date(h.changed_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                            <td className="py-1 pr-3">{h.changed_by_name || '—'}</td>
                            <td className="py-1 pr-3">{BUCKET_LABEL[h.bucket] || h.bucket}</td>
                            <td className="py-1 pr-3 font-semibold">{fmtChange(h.old_daily_cap, h.new_daily_cap)}</td>
                            <td className="py-1 pr-3">{fmtChange(h.old_new_lead_cap, h.new_new_lead_cap)}</td>
                            <td className="py-1">{fmtChange(h.old_not_connected_cap, h.new_not_connected_cap)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>}
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  )
}
